import React, { useRef, useEffect, useState } from 'react';

const NoiseVideo = React.forwardRef((props, ref) => {
    const {
        src,
        style,
        showWarning = true,
        noiseIntensity = 220,
        noiseFps = 4,
        ...videoProps
    } = props;

    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const [hasError, setHasError] = useState(false);
    const animationRef = useRef();
    const streamRef = useRef();

    // Генерация безопасного серого шума
    const generateSafeNoise = () => {
        if (!canvasRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const { width, height } = canvas;
        const imageData = ctx.createImageData(width, height);
        const pixels = imageData.data;

        for (let i = 0; i < pixels.length; i += 4) {
            const val = Math.floor(Math.random() * 56 + (noiseIntensity - 55));
            pixels[i] = pixels[i + 1] = pixels[i + 2] = val;
            pixels[i + 3] = 255;
        }

        ctx.putImageData(imageData, 0, 0);
    };

    // Управление потоком шума
    const handleNoiseStream = (start) => {
        if (!canvasRef.current || !videoRef.current) return;

        if (start) {
            // Установка размеров canvas
            canvasRef.current.width = videoRef.current.clientWidth || 640;
            canvasRef.current.height = videoRef.current.clientHeight || 360;

            // Остановка предыдущего потока
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
            }

            // Создание нового потока
            streamRef.current = canvasRef.current.captureStream(noiseFps);
            videoRef.current.srcObject = streamRef.current;

            // Анимация с контролируемой частотой
            const interval = 1000 / noiseFps;
            let lastTime = 0;

            const animate = (time) => {
                if (time - lastTime > interval) {
                    generateSafeNoise();
                    lastTime = time;
                }
                animationRef.current = requestAnimationFrame(animate);
            };

            animationRef.current = requestAnimationFrame(animate);
        } else {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
                streamRef.current = null;
            }
        }
    };

    // Обработчик ошибок
    const handleError = () => {
        if (!hasError) {
            setHasError(true);
            handleNoiseStream(true);
        }
    };

    // Сброс при изменении src
    useEffect(() => {
        setHasError(false);
    }, [src]);

    // Очистка
    useEffect(() => {
        return () => {
            handleNoiseStream(false);
        };
    }, []);

    // Передача ref
    React.useImperativeHandle(ref, () => videoRef.current);

    return (
        <div style={{ position: 'relative', ...style }}>
            <video
                ref={videoRef}
                {...videoProps}
                src={hasError ? undefined : src}
                onError={handleError}
                style={{ display: 'block', width: '100%', height: '100%' }}
            />

            <canvas
                ref={canvasRef}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    display: hasError ? 'block' : 'none',
                    pointerEvents: 'none'
                }}
            />

            {showWarning && hasError && (
                <div style={{
                    position: 'absolute',
                    bottom: 10,
                    left: 0,
                    right: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    padding: '8px',
                    textAlign: 'center',
                    fontSize: '14px'
                }}>
                    ⚠️ Видео файл не загружен.
                </div>
            )}
        </div>
    );
});

export default NoiseVideo;